// FI-1657 WebSocket client for live notification push.
//
// the server signals "go fetch" on every per-user notification write,
// the client treats it as a trigger to call the existing /notifications
// poll (count + list). payload shape is small on purpose; we don't move
// notification rows over the socket.
//
// auth: two-step. before each WS open the client POSTs to
// /bookings/notifications/ws-ticket with the user's normal JWT in
// Authorization: Bearer ..., and gets back a tiny short-lived JWT
// (~250 bytes, 60s TTL) carrying only {username, tenant, role}. that
// "ticket" rides as ?token=<...> on the wss:// URL. the user's main
// JWT is too large to fit in the URL on its own (admins carry the
// full tenant_options list, ~10 KB encoded) which exceeds API Gateway's
// URL length limit and the handshake fails. tickets stay tiny, fit
// in the URL, and live just long enough to complete the connect.
//
// reconnect uses full-jitter exponential backoff and runs forever. polling
// at the existing 60s cadence is the safety net any time the WS isn't open,
// so we never need a "give up" mode. the `onDisconnect` callback fires on
// every close so App.vue can restore fast polling immediately while we
// keep trying to reconnect in the background. each reconnect mints a
// fresh ticket since the previous one has either been used or expired.

const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 30000

// fleet-int-api hosts the ws-ticket endpoint. VUE_APP_API_URL is set
// per-stage at build time (https://api.fleet-integrations.com/ in prod,
// https://dev-api.fleet-integrations.com/ in dev).
const TICKET_PATH = 'bookings/notifications/ws-ticket'

async function fetchWsTicket(jwt) {
  const base = process.env.VUE_APP_API_URL || ''
  if (!base) throw new Error('VUE_APP_API_URL not set')
  const url = base.replace(/\/$/, '') + '/' + TICKET_PATH
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + jwt,
    },
  })
  if (!resp.ok) throw new Error('ws-ticket fetch failed: ' + resp.status)
  const body = await resp.json()
  // ApiSuccessResponse shape: {status, message, data: {ticket}}; some
  // older responses are flatter, handle both.
  const ticket = (body && body.data && body.data.ticket) || (body && body.ticket)
  if (!ticket) throw new Error('ws-ticket response missing ticket field')
  return ticket
}

function computeBackoff(attempt) {
  // full jitter: random(0, min(cap, base * 2**attempt))
  const cap = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * Math.pow(2, attempt))
  return Math.floor(Math.random() * cap)
}

export default class NotificationsWs {
  constructor() {
    this._url = null
    this._jwt = null
    this._ws = null
    this._attempt = 0
    this._stopped = true
    this._reconnectTimer = null
    this._onSignal = null
    this._onConnect = null
    this._onFallback = null
  }

  // start opens the connection and sets up reconnect handling.
  // - url:        wss:// endpoint (VUE_APP_WS_URL); empty disables WS entirely
  // - jwt:        JWT access token (carried via subprotocol)
  // - handlers:   { onSignal, onConnect, onDisconnect }
  //   onSignal(payload):    fired on every server message
  //   onConnect():          fired on every successful (re)connect
  //   onDisconnect():       fired on every close (failed connect or
  //                         dropped session) so the caller can restore
  //                         fast polling while we retry in the background
  start(url, jwt, handlers) {
    if (!url || !jwt) return false
    this._url = url
    this._jwt = jwt
    this._onSignal = (handlers && handlers.onSignal) || null
    this._onConnect = (handlers && handlers.onConnect) || null
    this._onDisconnect = (handlers && handlers.onDisconnect) || null
    this._stopped = false
    this._attempt = 0
    this._open()
    return true
  }

  stop() {
    this._stopped = true
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
    if (this._ws) {
      try { this._ws.close(1000, 'client stop') } catch (_) { /* noop */ }
      this._ws = null
    }
  }

  isOpen() {
    return !!(this._ws && this._ws.readyState === 1) // OPEN
  }

  _open() {
    if (this._stopped) return
    // fetch a fresh tiny ticket, then open the WS with it as ?token=.
    // see file-level comment for why we can't put the user's full JWT
    // in the URL directly. polling covers the gap if either the
    // ticket fetch or the WS handshake fails.
    fetchWsTicket(this._jwt)
      .then((ticket) => {
        if (this._stopped) return
        this._openWithTicket(ticket)
      })
      .catch(() => {
        // ticket fetch failed (network blip, JWT expired, /ws-ticket
        // not yet deployed on this stage): retry with backoff. caller
        // already started fast polling on construction so the user
        // doesn't see stale state in the gap.
        this._scheduleReconnect()
      })
  }
  _openWithTicket(ticket) {
    let ws
    try {
      const sep = this._url.indexOf('?') === -1 ? '?' : '&'
      const fullUrl = this._url + sep + 'token=' + encodeURIComponent(ticket)
      ws = new WebSocket(fullUrl)
    } catch (e) {
      // browser refused to construct (bad URL, mixed-content, etc.).
      this._scheduleReconnect()
      return
    }
    this._ws = ws
    ws.onopen = () => {
      this._attempt = 0
      if (this._onConnect) {
        try { this._onConnect() } catch (_) { /* noop */ }
      }
    }
    ws.onmessage = (ev) => {
      let payload = null
      try { payload = JSON.parse(ev.data) } catch (_) { payload = { raw: ev.data } }
      if (this._onSignal) {
        try { this._onSignal(payload) } catch (_) { /* noop */ }
      }
    }
    ws.onerror = () => { /* close fires after error; handle there */ }
    ws.onclose = (ev) => {
      this._ws = null
      // 1000 normal close (we initiated) means the caller asked us to
      // stop; don't reconnect, don't fire onDisconnect (caller already
      // knows it tore down the socket).
      if (this._stopped || ev.code === 1000) return
      // every other close means the WS is gone unexpectedly. tell the
      // caller right away so polling can pick up the slack while we
      // retry in the background. polling is the safety net, so we keep
      // retrying forever rather than ever "giving up."
      if (this._onDisconnect) {
        try { this._onDisconnect() } catch (_) { /* noop */ }
      }
      this._scheduleReconnect()
    }
  }

  _scheduleReconnect() {
    if (this._stopped) return
    const delay = computeBackoff(this._attempt)
    this._attempt += 1
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null
      this._open()
    }, delay)
  }
}
